/*
 * What Prove7 Protect offers on its first screen.
 *
 * WHY THIS IS A SEPARATE LIST FROM agentArchetypes.jsx
 * ---------------------------------------------------
 * `AGENT_ARCHETYPES` is the platform's own taxonomy — Let / AutoLet / ExteLet /
 * SysLet — and it is correct. It is also four words nobody outside this
 * codebase uses. A first-time visitor arriving from a public link does not know
 * what an ExteLet is and should not have to learn before they can start.
 *
 * So this is a presentation layer, not a second taxonomy. Every entry names an
 * agent people actually run, says plainly what it leaks today, and carries the
 * `archetype` key it resolves to. The creation machinery is unchanged: the page
 * hands the key to `agentCreateRoute()` exactly as every other entry point
 * does, so there is still ONE create surface and this cannot drift into being a
 * fifth archetype.
 *
 * The `leak` line is the whole reason a visitor is here: what this kind of
 * agent does with enterprise data today, before anything governs it.
 *
 * The five `pack` entries name the product they protect, because that is what
 * the customer came to find — they arrive knowing they run Copilot, not knowing
 * what an ExteLet is. The line beside each one describes how that class of
 * agent works; it is not a claim about the vendor's conduct, and the protection
 * is ours, not theirs.
 */

/**
 * The agents an enterprise already runs, and the two ways to bring your own.
 *
 * `mark` is the two-letter badge on a pack's card. It is written here rather
 * than taken from the name's initials because the badge people recognise is not
 * always the first letters of the words — and a badge is a brand mark, which is
 * content, not something to compute.
 *
 * `effort` is what we tell somebody it will take. It is a product claim and so
 * it is stated here, next to the copy it sits beside.
 *
 * `stepsLabel` is only on the two authored entries. A pack's card gets its step
 * count from the build script it actually runs, because a card promising seven
 * questions above a script that asks six is a lie told on first contact. These
 * two run no such script, so they say what they are instead.
 */
export const ESTATE_AGENTS = [
  {
    key: 'claude-cowork',
    pack: true,
    mark: 'CL',
    platformType: 'ANTHROPIC',
    externalAgentId: 'cowork-agent',
    prose: "Our Cowork agent reads and writes files for the team across finance and HR. Bring it under control: give it an identity of its own, narrow it to just the folders the work needs, and make sure nothing it reads is sent to a model outside our tenant.",
    archetype: 'extelet',
    name: 'Claude · Cowork',
    effort: '~6 min',
    blurb: 'Reads and writes your files on request. Sees whatever the person driving it can see.',
    leak: 'What it reads leaves your tenant to be answered.',
    touches: ['Files', 'Mail', 'Chat'],
  },
  {
    key: 'copilot',
    pack: true,
    mark: 'CP',
    platformType: 'MICROSOFT_COPILOT',
    externalAgentId: 'desktop-copilot',
    prose: "A copilot sits inside our mail, documents and chat and drafts across everything the signed-in person can open. Bring it under control: narrow it to the task rather than the person, and keep anything above Internal shut to it.",
    archetype: 'extelet',
    name: 'Microsoft 365 Copilot',
    effort: '~6 min',
    blurb: 'Sits inside mail, documents and chat, drafting across everything the signed-in person may open.',
    leak: 'Its reach is the person\u2019s, not the task\u2019s.',
    touches: ['Mail', 'Docs', 'Chat'],
  },
  {
    key: 'chatgpt',
    pack: true,
    mark: 'GP',
    platformType: 'OPENAI',
    externalAgentId: 'chatgpt-enterprise',
    prose: "Our people use ChatGPT across their work. Bring it under control: give it an identity of its own, keep anything above Internal shut to it, and keep a record of everything it was asked and answered.",
    archetype: 'extelet',
    name: 'ChatGPT Enterprise',
    effort: '~6 min',
    blurb: 'Answers across whatever your people paste into it, and whatever it is connected to.',
    leak: 'Reads widely, and keeps no account you can audit.',
    touches: ['Chat', 'Files'],
  },
  {
    key: 'gemini',
    pack: true,
    mark: 'GE',
    platformType: 'VERTEX_AI',
    externalAgentId: 'google-gemini',
    prose: "Gemini reaches across our Workspace. Bring it under control: narrow it to the task, keep customer data inside our tenant, and record what it touched.",
    archetype: 'extelet',
    name: 'Google Gemini',
    effort: '~6 min',
    blurb: 'Reaches across Workspace \u2014 mail, drive and docs \u2014 on the signed-in person\u2019s behalf.',
    leak: 'Workspace-wide reach, with no boundary of its own.',
    touches: ['Mail', 'Drive', 'Docs'],
  },
  {
    key: 'langgraph',
    pack: true,
    mark: 'LG',
    platformType: 'LANGGRAPH',
    externalAgentId: 'langgraph-app',
    prose: "We have agents our team built on LangGraph. Register them, give each an identity of its own, and hold every hop to the same limits: nothing leaving our tenant and a person approving anything irreversible.",
    archetype: 'extelet',
    name: 'LangChain · LangGraph',
    effort: '~6 min',
    blurb: 'Graphs your team wrote. Each hop calls out on its own, under one shared key.',
    leak: 'Nothing watches the hops in between.',
    touches: ['API', 'Tools'],
  },
  {
    key: 'coded',
    platformType: 'CUSTOM_REST',
    externalAgentId: 'team-coded-agent',
    prose: "We have an agent our own team wrote, running against an endpoint. Register it, give it an identity of its own, and hold it to the same limits as everything else: no data leaving our tenant and a person approving anything irreversible.",
    archetype: 'let',
    name: 'An agent your team coded',
    effort: '~3 min',
    stepsLabel: '1 gate',
    blurb: 'Authored here, with the full Build \u2192 Decide \u2192 Act lifecycle.',
    leak: 'Nothing watches what it does today.',
    touches: ['API'],
  },
  {
    key: 'runbook',
    prose: "Build an agent from the runbook our team follows today. Read the steps, draft the agent from them, and show me every step before anything runs.",
    archetype: 'autolet',
    name: 'Describe one, or hand us the runbook',
    effort: '~4 min',
    stepsLabel: 'drafted step by step',
    blurb: 'We draft it step by step and stop at anything we cannot work out.',
    leak: null,
    touches: ['Your words'],
  },
];

/** The four steps the visitor is responsible for. The seven proves are ours, and live in the lane. */
export const VISITOR_STEPS = [
  { key: 'choose',   name: 'Choose the agent',       hint: 'One your people already use.' },
  { key: 'limits',   name: 'Set the limits',          hint: 'What it must never do.' },
  { key: 'reviewer', name: 'Say who it answers to',   hint: 'Who sees a refusal.' },
  { key: 'run',      name: 'Watch it run',            hint: 'A first run you can stop.' },
];

/**
 * Which visitor step an Auto Create run status is sitting on.
 * Statuses are AutoCreateRun.java:52-59.
 */
export function stepForRunStatus(status) {
  switch (status) {
    case 'paused_gap':      return 'limits';
    case 'awaiting_review': return 'reviewer';
    case 'completed':       return 'run';
    case 'failed':
    case 'cancelled':
    case 'rejected':        return 'run';
    default:                return 'choose';
  }
}

export default ESTATE_AGENTS;
